import React from "react";
import { Box } from "@mui/material";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY } from "../../../utils/designTokens";

// Shared pieces of the Order Details page (screens/OrderDetail).

export const OD = {
  cancelledInk: "#8E1C14",
  cancelledBanner: "#3D3A35",
  lightRedText: "#FBD9D5",
  greenLine: "#CDEBD7",
};

/** A bordered card: 1px border, 22px radius. */
export function DetailCard({ children, sx, ...rest }) {
  return (
    <Box
      component="section"
      {...rest}
      sx={{ boxSizing: "border-box", minWidth: 0, bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: "22px", p: { xs: "20px", sm: "22px 24px" }, ...sx }}
    >
      {children}
    </Box>
  );
}

export function CardTitle({ id, children, sx }) {
  return (
    <Box component="h2" id={id} sx={{ m: 0, fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.25, color: C.ink, ...sx }}>
      {children}
    </Box>
  );
}

/** The outlined 44px button ("Get help", "Track driver →"). `dark` gives the 1.5px ink border. */
export const outlineButtonSx = (dark = false) => ({
  ...BUTTON_RESET,
  ...FOCUS_RING,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  minHeight: 44,
  px: 2,
  borderRadius: "12px",
  border: `1.5px solid ${dark ? C.ink : C.borderStrong}`,
  bgcolor: C.bg,
  color: C.ink,
  fontFamily: FONT_BODY,
  fontSize: 15,
  fontWeight: 600,
  textDecoration: "none",
  whiteSpace: "nowrap",
  transition: "background-color .15s ease, border-color .15s ease",
  "&:hover": { bgcolor: C.surface, borderColor: C.ink },
});

export const redButtonSx = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  minHeight: 44,
  px: 2.25,
  borderRadius: "12px",
  bgcolor: C.red,
  color: "#FFFFFF",
  fontFamily: FONT_BODY,
  fontSize: 15,
  fontWeight: 700,
  whiteSpace: "nowrap",
  transition: "background-color .15s ease",
  "&:hover:not(:disabled)": { bgcolor: C.redHover },
  "&:disabled": { opacity: 0.7, cursor: "default" },
};

const CHIP = {
  live: { color: C.redLink, bg: C.redTint, dot: C.red },
  complete: { color: C.green, bg: C.greenSoft, dot: C.green },
  cancelled: { color: OD.cancelledInk, bg: C.surface2, dot: OD.cancelledInk },
};

/** The status chip beside the title, with a 7px dot. */
export function StatusPill({ chip }) {
  const look = CHIP[chip.tone] || CHIP.live;
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: "7px", height: 30, px: 1.5, borderRadius: "999px", bgcolor: look.bg, color: look.color, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: look.dot }} />
      {chip.label}
    </Box>
  );
}

/** A small uppercase grey heading ("RESTAURANT"). */
export function GroupHeading({ children, sx }) {
  return (
    <Box component="h3" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: C.muted, ...sx }}>
      {children}
    </Box>
  );
}
